import React, { memo, useState } from 'react';
import { cssMap } from '@compiled/react';
import noop from 'lodash/noop';
import { Drawer, DrawerCloseButton, DrawerContent, DrawerSidebar } from '@atlaskit/drawer';

import { Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type Stage, ProgressTracker, type Stages } from '@atlaskit/progress-tracker';

import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import type { CreateFlowVariant, StageProps } from '../../../common/types.tsx';
import CreateSubpage from './create/index.tsx';
import ReviewSubpage from './review/index.tsx';
import ReciprocalPromptSubpage from './reciprocal-prompt/index.tsx';
import ConfirmationSubpage from './confirmation/index.tsx';
import ConnectSubpage from './connect/index.tsx';

type StepSequence = string[];
type StepNameMap = { [key: string]: string };

const UNKNOWN_STEP_ORDER: StepSequence = ['create', 'unknown'];
const OAUTH1_STEP_ORDER: StepSequence = ['create', 'review', 'reciprocal-prompt', 'confirmation'];
const OAUTH2_STEP_ORDER: StepSequence = ['create', 'review', 'connect'];

// TODO: Localise these names
const STEP_NAMES: StepNameMap = {
	create: 'Create link',
	review: 'Review link',
	'reciprocal-prompt': 'Create reciprocal',
	connect: 'Secure connection',
	confirmation: 'Confirmation',
	unknown: 'Configure link',
};

const determineStepPercentage = (
	currentStep: string,
	targetStep: string,
	sequence: StepSequence,
): number => {
	if (currentStep === 'manual-create' && targetStep === 'create') {
		return 50; // Special case for manual create step
	}

	const currentIndex = sequence.indexOf(currentStep);
	const targetIndex = sequence.indexOf(targetStep);

	if (currentIndex > targetIndex) {
		return 100;
	}
	if (currentIndex === targetIndex) {
		return 0;
	}
	return 0;
};

const determineStepStatus = (
	currentStep: string,
	targetStep: string,
	sequence: StepSequence,
): Stage['status'] => {
	if (currentStep === 'manual-create' && targetStep === 'create') {
		return 'current'; // Special case for manual create step
	}

	const currentIndex = sequence.indexOf(currentStep);
	const targetIndex = sequence.indexOf(targetStep);

	if (currentIndex > targetIndex) {
		return 'visited';
	}
	if (currentIndex === targetIndex) {
		return 'current';
	}
	return 'unvisited';
};

const buildSteps = (step: string, sequence: StepSequence): Stages => {
	return sequence.map((s) => ({
		id: s,
		label: STEP_NAMES[s],
		percentageComplete: determineStepPercentage(step, s, sequence),
		status: determineStepStatus(step, s, sequence),
		href: '#',
	}));
};

const buildProgressTrackerItems = (step: string, variant: CreateFlowVariant | null): Stages => {
	if (variant === null) {
		return buildSteps(step, UNKNOWN_STEP_ORDER);
	}

	switch (variant) {
		case 'OAUTH1.0':
			return buildSteps(step, OAUTH1_STEP_ORDER);
		case 'TUNNEL':
			return buildSteps(step, OAUTH1_STEP_ORDER); // Tunnel uses the same steps as OAUTH1.0
		case 'OAUTH2.0':
			return buildSteps(step, OAUTH2_STEP_ORDER);
		default:
			return [];
	}
};

type FlowContentProps = {
	state: StageProps;
	onCancel: () => void;
	onStateChange: (newState: StageProps) => void;
	// setConfirmationStage: We don't have a transition to confirmation from the review step because
	// we're redirected here from the remote application. This is handled in main.tsx instead.
};

const styles = cssMap({
	container: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		maxWidth: '800px',
		margin: '0 auto',
	},
});

const FlowContent = memo((props: FlowContentProps) => {
	switch (props.state.kind) {
		case 'create':
		case 'manual-create':
			return (
				<CreateSubpage onError={noop} onSuccess={props.onStateChange} onCancel={props.onCancel} />
			);
		case 'review':
			return (
				<ReviewSubpage
					state={props.state}
					onSuccess={props.onStateChange}
					// TODO: Handle error properly
					onError={noop}
					onCancel={props.onCancel}
				/>
			);
		case 'reciprocal-prompt':
			return <ReciprocalPromptSubpage state={props.state} />;
		case 'connect':
			return <ConnectSubpage state={props.state} onSuccess={props.onStateChange} onError={noop} />;
		case 'confirmation':
			return <ConfirmationSubpage state={props.state} />;
		default:
			return <Text>Placeholder</Text>;
	}
});

export type CreateFlowDialogProps = {
	isOpen: boolean;
	onExit: () => void;
	startingStage?: StageProps;
};

const DEFAULT_CREATE_STAGE: StageProps = { kind: 'create' };

export const CreateFlowDialog = (props: CreateFlowDialogProps) => {
	// Use the provided starting stage or default to the create stage
	const [flowState, setFlowState] = useState<StageProps>(
		props.startingStage || DEFAULT_CREATE_STAGE,
	);

	const onCancel = () => {
		props.onExit();
		setFlowState(DEFAULT_CREATE_STAGE);
	};

	useIntl();

	if (fg('applinks-933-experimental-list-page')) {
		return (
			<Drawer
				testId="applinks-administration.ui.list.creation-flow-dialog.drawer"
				width="full"
				onClose={() => onCancel()}
				isOpen={props.isOpen}
				label="Applinks Create Drawer"
			>
				<DrawerSidebar>
					<DrawerCloseButton />
				</DrawerSidebar>
				<DrawerContent>
					<Stack space="space.200" xcss={styles.container}>
						<ProgressTracker
							items={buildProgressTrackerItems(
								flowState.kind,
								'variant' in flowState ? flowState.variant : null,
							)}
							spacing="comfortable"
						/>
						<FlowContent state={flowState} onCancel={onCancel} onStateChange={setFlowState} />
					</Stack>
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<Stack space="space.200" xcss={styles.container}>
			<FlowContent state={flowState} onCancel={props.onExit} onStateChange={setFlowState} />
		</Stack>
	);
};
